<template>
	<div class="inner-page add-edit-form">

		<template v-if="['admin'].includes($page.props.user.user_type)">

			<AppHead :title="form ? `Edit PMS Form` : `New PMS Form`" />
			
			<PageHeader 
				:title="form ? `Edit PMS Form` : `New PMS Form`"
				:desc="`${form ? 'Edit the form' : 'Create a new form'} to use in the Post Market Surveillance step of a case.`"
				:hasButton="false"
				:showHelper="false"
				:backButton="true"
				:reverseRightBlock="true"
				/>

			<div class="form-inputs-container">

				<InputGroup
					title="Name of the form"
					desc="This name will be shown to the users of the Case."
					>

					<InputText
						label="Form name"
						type="text"
						v-model="formData.name"
						autocomplete="disabled"
						/>

				</InputGroup>

				<InputGroup
					title="Form Status"
					desc="Enable or disable the form. If disabled, the form will not be available to use in the Post Market Surveillance step of a case, but you will still be able to view past results of forms filled in."
					>

					<InputOnOffPill
						label="Status"
						v-model="formData.active"
						/>

				</InputGroup>

				<InputGroup
					title="Upload Type"
					desc="If this form is of upload type then you will not be able to assign blocks to it, instead you'll be able to make use of the encrypted upload feature only."
					>

					<InputOnOffPill
						label="Is this an upload form?"
						v-model="formData.upload"
						/>

				</InputGroup>

				<InputGroup
					v-if="formData.upload"
					title="Upload description"
					desc="Please give a description of what the user should be uploading. This will be shown above the upload box to the user."
					>

					<InputTextarea
						label="Upload description text"
						v-model="formData.uploadDesc"
						autocomplete="disabled"
						/>

				</InputGroup>

				<InputGroup
					title="Trigger time points"
					desc="When should this form be triggered?"
					>

					<InputFancyRadio
						label="Time point mode"
						id="timePointMode"
						v-model="formData.timepoints.mode"
						:options="timepointOptions"
						/>

					<div 
						v-if="formData.timepoints.mode === 'time-intervals'"
						class="time-intervals-container"
						>

						<div 
							class="time-interval"
							v-for="(point, index) in formData.timepoints.points"
							:key="index"
							>
							<InputText
								label="Weeks since surgery"
								type="number"
								v-model="point.weeks"
								autocomplete="disabled"
								:noMargin="true"
								/>
							<Button
								label="Remove"
								color="red"
								size="medium"
								icon="trash"
								:forceHeight="true"
								@click="formData.timepoints.points.splice(index, 1)"
								/>
						</div>

						<Button
							label="Add time point"
							color="green"
							size="regular"
							icon="plus"
							@click="addTimePoint"
							/>
					</div>
					

				</InputGroup>

				<InputGroup
					title="Distributor access"
					desc="Which distributors should have access to this form?"
					>

					<MultiSelect
						label="Distributors"
						v-model="formData.teamAccess"
						:options="teams"
						:preselectAll="true"
						:showSelectedList="false"
						/>

				</InputGroup>



				<InputGroup
					title=""
					desc=""
					:skipWhiteBox="true"
					:noLine="true"
					>

					<ValidationErrors />

					<Button
						:label="form ? `Save` : `Create`"
						color="green"
						size="regular"
						icon="plus" 
						@click="submit"
						/>
				</InputGroup>

			</div>

		</template>
	
	</div>
</template>

<script>

import AppLayout from "@/Layouts/AppLayout.vue"
import AppHead from "@/Components/AppHead"
import PageHeader from "@/Components/PageHeader"
import Subtitle from "@/Components/Subtitle"
import Placeholder from "@/Components/Placeholder"
import Button from "@/Components/Button"
import { Link } from '@inertiajs/inertia-vue3'
import InputText from "@/Components/InputText"
import InputTextarea from "@/Components/InputTextarea"
import InputGroup from "@/Components/InputGroup"
import InputOnOffPill from "@/Components/InputOnOffPill"
import InputFancyRadio from "@/Components/InputFancyRadio"
import MultiSelect from "@/Components/MultiSelect"
import ValidationErrors from "@/Components/ValidationErrors"

export default {
	components: {
		AppLayout,
		AppHead,
		PageHeader,
		Subtitle,
		Placeholder,
		Button,
		Link,
		InputText,
		InputTextarea,
		InputGroup,
		InputOnOffPill,
		InputFancyRadio,
		MultiSelect,
		ValidationErrors
	},
	layout: AppLayout,
	props: {
		formId: {
			type: [Number, Boolean],
			default: false // if false then this is a 'create' type form. if an id is supplied then we assume its an 'edit' type form
		},
		form: {
			type: [Object, Boolean],
			default: false
		},
		teams: {
			type: [Array, Boolean],
			default: false
		}
	},
	data: () => {
		return {
			formData: {
				name: '',
				active: false,
				upload: false,
				uploadDesc: '',
				timepoints: {
					mode: '',
					points: []
				},
				teamAccess: []
			},
			errors: [],
			timepointOptions: [
				{
					text: 'Once, before PMS stage',
					desc: 'Once, when a case is created. The surgeon will be able to fill out this form once on a brand new case, before PMS stage has been reached, and obviously before surgery date.',
					value: 'once-before-pms'
				},
				{
					text: 'Once, after surgery date',
					desc: 'Once, immediately when the PMS stage is reached after the surgery date. The surgeon will be notified immediately to complete the form if the PMS stage has been activated and the date is after the surgery date.',
					value: 'once-after-surgery'
				},
				{
					text: 'User triggered',
					desc: 'User triggered at any time after surgery date, as many times a needed',
					value: 'user-triggered'
				},
				{
					text: 'At a custom time interval',
					desc: 'Define time intervals in weeks since surgey date, and the form will be triggered at those intervals. Notifications will be sent to the surgeon when each time point is reached.',
					value: 'time-intervals'
				}
			]
		}
	},
	mounted(){
		
		if(this.formId){
			// edit
			console.log(this.form)
			this.formData.name = this.form.name
			this.formData.active = this.form.active
			this.formData.upload = this.form.upload
			this.formData.uploadDesc = this.form.upload_description
			this.formData.timepoints = this.form.timepoint

			if(this.form.teams){
				this.formData.teamAccess = this.form.teams.map(team => team.id)
			}

			console.log('formData', this.formData)
		} 

	},
	methods: {
		checkFormIsValid(){
			// check if form is valid
			if(this.formData.name.length < 1){
				return false
			}
		},
		submit(){
			console.log(this.formData)

			if(this.formId){
				// edit

				this.$inertia.post(`/admin/pms-forms/${this.formId}/edit`, this.formData)

			} else {
				// create

				this.$inertia.post('/admin/pms-forms/create', this.formData)
			}
		},
		addTimePoint(){
			this.formData.timepoints.points.push({
				weeks: 0
			})
		},
	},
};
</script>

<style lang="sass">

.add-edit-form
	.time-intervals-container
		max-width: 300px
		width: 100%
		display: flex
		flex-direction: column
		gap: 10px

		.time-interval
			display: flex
			gap: 10px
			align-items: flex-end
			
		

</style>